import React, { useState, useEffect } from "react"
import classNames from "classnames"

import { ReactComponent as HeartIcon } from "../../../images/icons/heartIcon.svg"
import { ReactComponent as HeartFilledIcon } from "../../../images/icons/heart-filled.svg"
import ConfirmCancelPopup from "../../Popup/ConfirmCancelPopup"
import styles from "./index.module.scss"
import useProfileImage from "../../../hooks/useProfileImage"

type FreelancerData = {
  name?: string
  title?: string
  photo?: string
  description?: string
}

type FreelancerCardProps = {
  /** External styles */
  classes?: any
  /** Company Avatar */
  freelancerAvatar: string
  /** Company Name */
  freelancerName: string
  /** External ID (Upwork personId or profile_key) */
  freelancerUuid: string
  /** Profile key or id for URL (e.g. Upwork profile_key) */
  profileKey?: string
  /** Chat Uuid */
  chatUuid: string
  /** Description */
  description: string
  /** Card shows up for owner */
  isOwned: boolean
  /** Callback for click on the card */
  onClick: (e: React.MouseEvent) => void
  /** Callback when favourite is toggled (receives profile_key, shouldFavorite, and optional freelancer data) */
  onFavoriteToggle?: (
    profileKey: string,
    isFavorite: boolean,
    freelancerData?: FreelancerData,
  ) => Promise<void>
  /** Whether this freelancer is in user's favorites */
  isFavorite?: boolean
  /** Freelancer's UUID from the database (optional, for reference) */
  dbUuid?: string | null
  skills: string
}
const FreelancerCard = ({
  classes,
  freelancerAvatar,
  freelancerName,
  freelancerUuid,
  description,
  isOwned,
  onClick,
  chatUuid,
  onFavoriteToggle,
  isFavorite: initialFavorite = false,
  dbUuid,
  profileKey,
  skills,
}: FreelancerCardProps) => {
  const [favorite, setFavorite] = useState(initialFavorite)
  const [isLoading, setIsLoading] = useState(false)
  const [confirmPopupOpened, setConfirmPopupOpened] = useState(false)
  const avatarSrc = useProfileImage(freelancerAvatar)

  // Sync local state when the isFavorite prop changes (e.g., after data refetch)
  useEffect(() => {
    setFavorite(initialFavorite)
  }, [initialFavorite])

  const toggleFavorite = async (shouldFavorite: boolean) => {
    if (isLoading || !onFavoriteToggle) return

    // Use profile_key for the API call (required for Upwork profile fetching)
    const identifier = profileKey || freelancerUuid
    if (!identifier) return

    setIsLoading(true)
    try {
      // Include freelancer data when adding to favorites
      const freelancerData: FreelancerData | undefined = shouldFavorite
        ? {
            name: freelancerName,
            title: skills,
            photo: freelancerAvatar,
            description: description,
          }
        : undefined
      await onFavoriteToggle(identifier, shouldFavorite, freelancerData)
      setFavorite(shouldFavorite)
    } catch (error) {
      console.error("Failed to toggle favorite:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (favorite) {
      // Show confirmation popup before removing from favorites
      setConfirmPopupOpened(true)
    } else {
      // Add to favorites directly
      toggleFavorite(true)
    }
  }

  return (
    <React.Fragment>
      {confirmPopupOpened && (
        <ConfirmCancelPopup
          cancelMessage="Are you sure you want to delete user from favorites?"
          setIsPopupOpened={setConfirmPopupOpened}
          handleCancel={() => {
            toggleFavorite(false)
            setConfirmPopupOpened(false)
          }}
        />
      )}

      <div
        className={classNames(styles.card, classes)}
        onClick={(e) => !confirmPopupOpened && onClick(e)}
      >
        <div className={styles.block}>
          <div
            className={styles.companyAvatar}
            style={{
              backgroundImage: `url(${avatarSrc})`,
            }}
          />
          <span className={styles.companyName}>{freelancerName}</span>
        </div>
        {skills && <p className={styles.companyType}>{skills}</p>}
        {description && <p className={styles.description}>{description}</p>}
        {!isOwned && (
          <>
            <div
              className={classNames(
                styles.heartButton,
                favorite && styles.favorite,
                isLoading && styles.loading,
              )}
              onClick={handleFavoriteClick}
              role="button"
              aria-label={
                favorite ? "Remove from favorites" : "Add to favorites"
              }
              title={favorite ? "Remove from favorites" : "Add to favorites"}
            >
              {favorite ? <HeartFilledIcon /> : <HeartIcon />}
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  )
}
export default FreelancerCard
